import { MarketNotReady } from './MarketScopeTabs';
import { useMarketReady } from './marketScope';
import { useState } from 'react';
import { Badge, Card, EmptyState, ErrorState, Spinner, Table, Td, Th, cx } from '@/components/ui';
import { formatKrwPlain } from '@/lib/format';
import { TradingNav } from './TradingNav';
import type { SectorStock } from './stockTypes';
import { StockDetailModal } from './StockDetailModal';
import {
  PriceVolumeTh,
  QuoteCell,
  StackedSortTh,
  StockIdentity,
  SWING_COLUMN_NOTE,
  SwingCell,
} from './components';
import { formatVolume } from './formatVolume';
import { quoteColorClass, resolveQuote, type QuoteView } from './priceDisplay';
import { sortRows, type SortState } from './tableSort';
import { usePrices, useSectorStocks, useSectors } from './useStocks';

/**
 * 섹터 표에서 정렬할 수 있는 열. '종목'은 기본 정렬(시가총액 순위)이라 없다.
 *
 * volume 은 2026-09-10 에 붙었다. 거래량이 현재가 아래 둘째 줄에 있어
 * 열 제목이 하나뿐이었는데, 그 줄로도 정렬하고 싶다는 요청이었다.
 */
type SectorSortKey = 'price' | 'volume' | 'changeAmount' | 'changeRate' | 'swing3m' | 'swing6m';

/**
 * 섹터별 종목. (2026-08-23 사용자 요청)
 *
 * "반도체AI 관련해서 카테고리를 선택하면 해당 종목의 코스피 기준 상위 30개 정도를
 *  표시. 표시할 때는 주가도 당연히 표시"
 *
 * 🔴 상위의 기준은 시가총액이다 (사용자 확정)
 *   시가총액을 아직 못 구한 종목은 뒤로 밀리되 목록에서 빠지지는 않는다.
 *
 * 주가는 15초마다 갱신되는 시세 훅에서 온다. 시세가 없으면(장 시작 전 등)
 * 마지막 종가를 대신 보여주고 그 사실을 밝힌다.
 */
export function SectorsPage() {
  const sectors = useSectors();
  const [selected, setSelected] = useState<string | null>(null);
  // 행을 누르면 추천 목록과 같은 종목 모달이 뜬다 (2026-08-28 사용자 요청)
  const [openStockId, setOpenStockId] = useState<number | null>(null);
  // 가격 열 정렬 (2026-08-28 사용자 요청). 기본은 시가총액 순위 순
  const [sort, setSort] = useState<SortState<SectorSortKey>>({ key: null, dir: 'asc' });
  const ready = useMarketReady();

  // 첫 섹터를 기본 선택한다. 빈 화면부터 보여주면 무엇을 눌러야 할지 모른다.
  const code = selected ?? sectors.data?.[0]?.code ?? null;
  const stocks = useSectorStocks(code);

  const stockIds = stocks.data?.map((s) => s.stockId) ?? [];
  const prices = usePrices(stockIds);

  /*
    정렬 전에 시세를 행에 합쳐둔다 - 화면에 보이는 값(시세 없으면 마지막 종가)과
    정렬에 쓰는 값이 같아야 한다. 따로 계산하면 "현재가 순으로 눌렀는데
    종가 행만 이상한 자리에 있는" 표가 된다.
    순위 번호는 시가총액 순위에 고정한다 - 정렬해도 행을 따라간다.
  */
  const rows = (stocks.data ?? []).map((stock, index) => {
    const price = prices.data?.find((p) => p.stockId === stock.stockId);
    // 섹터 표는 전일 종가를 따로 주지 않는다. 등락은 시세 응답이 계산해 온 값을 쓴다
    const view = resolveQuote(price, stock.lastClose, null);
    return { stock, rank: index + 1, view, shown: view.price, change: view.changeRate };
  });
  const stale = prices.data?.[0]?.stale ?? true;

  const sorted = sortRows(
    rows,
    sort,
    (row, key) => {
      switch (key) {
        case 'price':
          return row.shown;
        case 'volume':
          // 화면에 찍히는 것과 같은 값이다 - 마지막 일봉의 거래량
          return row.stock.lastVolume;
        case 'changeAmount':
          // 🔴 금액과 %는 정렬 결과가 다르다. 누른 이름의 값으로 줄 세운다
          return row.view.change;
        case 'swing3m':
          return row.stock.swing3m;
        case 'swing6m':
          return row.stock.swing6m;
        default:
          return row.change;
      }
    },
    (a, b) => a.rank - b.rank,
  );

  /*
    🔴 국내 전용 화면이라 탭에서는 숨지만 주소로 직접 들어올 수 있다 (2026-09-20)
      전에는 안내를 배너로 얹고 국내 섹터를 그대로 그렸다. 업종 분류(td_sector_map)에
      미국 종목이 한 행도 없어 미국 섹터는 만들 수 없다 - 시드가 생기면 이 분기가 사라진다 (I-67).
   */
  if (!ready) {
    return (
      <div className="flex flex-col gap-4">
        <TradingNav />
        {/* 🔴 막힌 이유가 계좌가 아니라 섹터 분류다 (MarketNotReady 주석) */}
        <MarketNotReady reason="미국 종목은 주도주 · 섹터 화면이 쓰는 업종 분류가 아직 없습니다. 미국 스윙 계좌의 다섯 섹터는 그 계좌만 쓰는 손분류라 여기에는 나오지 않습니다." />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <TradingNav />

      <header>
        <h1 className="text-xl font-semibold">섹터</h1>
        <p className="mt-1 text-sm text-fg-muted">
          코스피·코스닥 종목을 테마별로 묶어 시가총액 순으로 봅니다. 한 종목이 여러
          섹터에 들어갈 수 있습니다 (삼성전자는 반도체이면서 AI입니다).
          {/*
            🔴 자동 종목 선별은 코스피만 본다 (2026-09-10)
              섹터 화면에 코스닥이 들어왔다고 추천까지 코스닥을 보는 것은 아니다.
              그 둘이 같다고 읽히면 "왜 코스닥은 추천에 안 뜨지" 가 된다.
          */}
          {/*
            🔴 분류의 출처와 한계를 밝힌다 (2026-09-20)
              td_sector_map 은 마이그레이션으로 손수 넣은 시드다 — V113(코스피 유동성 상위 200) ·
              V121·V122(커버리지 30.3%) · V125(코스닥 상위 300 중 확신이 서는 것만).
              즉 상장 종목 대부분이 어느 섹터에도 없다. 그 사실을 안 적으면
              "이 테마의 종목은 이게 전부" 로 읽힌다.
          */}
          <span className="mt-1 block text-xs text-fg-subtle">
            자동 종목 선별(추천)은 코스피만 대상입니다. 코스닥은 여기서 보기만 합니다.
            🔴 섹터 분류는 손으로 넣은 목록이라 <strong>전 종목을 덮지 않습니다</strong> —
            분류되지 않은 종목은 어느 테마에도 나오지 않습니다.
          </span>
        </p>
      </header>

      {sectors.isLoading && <Spinner label="섹터를 불러오는 중" />}
      {sectors.error && (
        <ErrorState message={sectors.error.message} onRetry={() => void sectors.refetch()} />
      )}

      {sectors.data && sectors.data.length === 0 && (
        <Card>
          <EmptyState
            title="분류된 섹터가 없습니다"
            description="섹터 분류가 아직 채워지지 않았습니다."
          />
        </Card>
      )}

      {sectors.data && sectors.data.length > 0 && (
        <>
          <div className="flex flex-wrap gap-1">
            {sectors.data.map((sector) => (
              <button
                key={sector.code}
                type="button"
                onClick={() => setSelected(sector.code)}
                className={cx(
                  'rounded-full px-3 py-1 text-sm transition-colors',
                  sector.code === code
                    ? 'bg-brand text-brand-fg'
                    : 'bg-surface-hover text-fg-muted hover:text-fg',
                )}
              >
                {sector.code}
                <span className="ml-1 text-xs opacity-70">{sector.stockCount}</span>
              </button>
            ))}
          </div>

          {/* 장 상태는 종목마다 같으므로 아무 하나에서 읽으면 된다 */}
          {stocks.isLoading && <Spinner label="종목을 불러오는 중" />}
          {/* 🔴 제목의 수는 탭 배지의 수와 같아야 한다. 상한을 없앤 이유가 그것이다 */}
          {stocks.data && (
            <Card title={`${code} · ${stocks.data.length}종목`}>
              <Table>
                <thead>
                  <tr>
                    <Th>종목</Th>
                    {/* 🔴 제목 두 줄(2026-10-05) - 폭을 줄여 왕복성 열 자리를 만든다(네 열 · 폰 가로 밀림 9/12 기록) */}
                    <PriceVolumeTh
                      priceKey="price"
                      volumeKey="volume"
                      sort={sort}
                      onSort={setSort}
                      stale={stale}
                      twoLine
                    />
                    <StackedSortTh
                      topLabel="대비"
                      topKey="changeAmount"
                      bottomLabel="등락률"
                      bottomKey="changeRate"
                      sort={sort}
                      onSort={setSort}
                      twoLine
                    />
                    <StackedSortTh
                      topLabel="왕복 3개월"
                      topKey="swing3m"
                      bottomLabel="6개월"
                      bottomKey="swing6m"
                      sort={sort}
                      onSort={setSort}
                      twoLine
                    />
                  </tr>
                </thead>
                <tbody>
                  {sorted.map((row) => (
                    <SectorRow
                      key={row.stock.stockId}
                      rank={row.rank}
                      stock={row.stock}
                      view={row.view}
                      onOpen={() => setOpenStockId(row.stock.stockId)}
                    />
                  ))}
                </tbody>
              </Table>

              {/*
                🔴 2026-09-20 두 문장을 고쳤다

                  ① "탭에 적힌 수만큼 전부 나옵니다" 는 보장되지 않는다.
                     탭 숫자는 countByCode() 라 상장폐지 필터가 없고, 목록은 DELISTED 를 뺀다.
                     매핑에 상장폐지가 남아 있으면 배지(46)와 행 수(45)가 어긋난다.
                  ② "번호가 시가총액 순위" 도 정확하지 않다. 번호는 정렬 뒤 인덱스라,
                     시가총액을 못 구한 종목(상장주식수 미수신 등)은 맨 뒤에서 번호를 받는다.
              */}
              <p className="mt-3 text-xs text-fg-subtle">
                열 제목을 누르면 그 열로 정렬되고 (오름 → 내림 → 기본), 거래량도 눌러서
                정렬할 수 있습니다. 기본 순서는 시가총액이 큰 순입니다. 상장폐지된 종목은
                빠지므로 탭의 수보다 적게 나올 수 있습니다.
              </p>
              <p className="mt-1 text-xs text-fg-subtle">
                종목명 앞의 번호는 이 목록에서의 순서입니다 — 기본 정렬에서는 시가총액
                순위와 같지만, 시가총액을 아직 못 구한 종목은 맨 뒤에 놓입니다. 금액은
                종목을 눌러 나오는 창에
                있습니다. 현재가는 15초마다 갱신되며, 장이 닫혀 있으면 마지막 종가입니다.
                거래량은 마지막 일봉의 값이라 실시간이 아닙니다.
              </p>
              <p className="mt-1 text-xs text-fg-subtle">
                {SWING_COLUMN_NOTE}
              </p>
            </Card>
          )}
        </>
      )}

      <StockDetailModal stockId={openStockId} onClose={() => setOpenStockId(null)} />
    </div>
  );
}

function SectorRow({
  rank,
  stock,
  view,
  onOpen,
}: {
  /** 시가총액 순위. 정렬을 바꿔도 이 번호는 행을 따라간다 */
  rank: number;
  stock: SectorStock;
  /** 현재가·대비·등락률과 그 색. 시세가 없으면 마지막 종가로 채워져 온다 */
  view: QuoteView;
  onOpen: () => void;
}) {
  /*
    행 전체가 버튼이다. 추천 목록과 같은 동작 - 눌러서 차트를 보고
    관심종목에 담는다. 키보드로도 열려야 해서 tabIndex 와 Enter 를 받는다.
  */
  return (
    <tr
      onClick={onOpen}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          onOpen();
        }
      }}
      tabIndex={0}
      className="cursor-pointer transition-colors hover:bg-surface-hover"
    >
      {/* 🔴 표식은 아래 줄로. 같은 줄에 두면 좁은 폭에서 종목명이 접힌다 */}
      <Td>
        <div className="flex items-baseline gap-2">
          <span className="text-xs text-fg-subtle tabular">{rank}</span>
          <span className="font-medium">{stock.name}</span>
        </div>
        {/* 거래소 + 종목코드. 아래 줄로 내려야 좁은 화면에서 종목명이 안 접힌다 */}
        <StockIdentity market={view.market ?? stock.market} symbol={stock.symbol} />
        {stock.universe && (
          <div className="mt-1">
            <Badge tone="info">
              <span className="text-[10px]">선별 대상</span>
            </Badge>
          </div>
        )}
      </Td>
      {/*
        🔴 시가총액 열은 2026-09-12 사용자 요청으로 뺐다
          폰에서 네 열은 가로로 밀린다. 그리고 이 값은 "지금 살까" 를 정할 때 보는
          숫자가 아니라 종목을 알아볼 때 한 번 보는 숫자다. 종목을 눌러서 뜨는
          모달로 옮겼다. 목록의 기본 순서는 여전히 시가총액이고 그 순위가
          종목명 앞의 번호다 - 열이 없어도 순서는 읽힌다.
      */}
      <Td align="right" numeric>
        <span className={quoteColorClass(view)}>
          {view.price === null ? '-' : formatKrwPlain(view.price)}
        </span>
        {/*
          거래량. 현재가 아래 둘째 줄이다 (2026-09-10 사용자 요청).
          관심종목과 같은 자리·같은 방식이다 - 토스 현재가 API 에 거래량이 없어
          마지막 일봉의 값을 쓴다.
        */}
        {stock.lastVolume !== null && (
          <span className="block text-[11px] leading-tight text-fg-subtle">
            {formatVolume(stock.lastVolume)}
          </span>
        )}
      </Td>
      <Td align="right" numeric>
        {/* 🔴 현재가는 앞 열이 이미 찍었다. 여기서 또 그리면 한 줄에 두 번 나온다 */}
        <QuoteCell view={view} showPrice={false} stacked />
      </Td>
      {/* 왕복성 3개월 / 6개월 (2026-10-05 사용자 요청 · 1년 환산 - SwingCell 주석) */}
      <Td align="right" numeric>
        <SwingCell threeMonth={stock.swing3m} sixMonth={stock.swing6m} />
      </Td>
    </tr>
  );
}
